import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Share2, 
  Instagram, 
  Facebook,
  Twitter,
  Hash,
  Calendar,
  BarChart3,
  ArrowLeft,
  RefreshCw,
  Copy,
  Download,
  Clock,
  TrendingUp,
  Plus,
  Trash2,
  Edit,
  CheckCircle2,
  Linkedin,
  Link2,
  Unlink,
  Settings,
  Eye,
  Heart,
  MessageCircle,
  Send,
  ThumbsUp,
  Layers,
  User,
  Construction,
  ChevronRight,
  Home,
  Image,
  ExternalLink,
  Loader2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ToolSEOContent from "@/components/ToolSEOContent";
import WhatsAppChat from "@/components/WhatsAppChat";
import RelatedTools from "@/components/RelatedTools";
import { useUser } from "@/contexts/UserContext";

// Tabs that are coming soon (need OAuth to work)
const COMING_SOON_TABS = ["post-scheduler", "analytics-dashboard", "cross-posting"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

function SocialMediaToolsContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoggedIn } = useUser();
  const [activeTool, setActiveTool] = useState("hashtag-generator");

  // Get tool from URL params
  useEffect(() => {
    const toolFromUrl = searchParams.get('tool');
    if (toolFromUrl) {
      setActiveTool(toolFromUrl);
    }
  }, [searchParams]);

  // Hashtag Generator State
  const [hashtagTopic, setHashtagTopic] = useState("");
  const [generatedHashtags, setGeneratedHashtags] = useState(null);
  const [loadingHashtags, setLoadingHashtags] = useState(false);
  // Bulk hashtag generation
  const [bulkHashtagTopics, setBulkHashtagTopics] = useState("");
  const [bulkHashtagResults, setBulkHashtagResults] = useState([]);
  const [loadingBulkHashtags, setLoadingBulkHashtags] = useState(false);

  // Post Generator State
  const [postTopic, setPostTopic] = useState("");
  const [generatedPosts, setGeneratedPosts] = useState(null);
  const [loadingPosts, setLoadingPosts] = useState(false);

  // Post Scheduler State
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [newPost, setNewPost] = useState({
    content: "",
    platform: "instagram",
    scheduledDate: "",
    scheduledTime: "",
    status: "scheduled"
  });
  const [showPostPreview, setShowPostPreview] = useState(false);

  // Connected Accounts State
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null);

  // Video Downloader State
  const [videoUrl, setVideoUrl] = useState("");
  const [videoResult, setVideoResult] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);

  // Instagram Image Downloader State
  const [igImageUrl, setIgImageUrl] = useState("");
  const [igImageResult, setIgImageResult] = useState(null);
  const [igImageLoading, setIgImageLoading] = useState(false);

  // Instagram Profile Scraper State
  const [igUsername, setIgUsername] = useState("");
  const [igProfileData, setIgProfileData] = useState(null);
  const [igProfileLoading, setIgProfileLoading] = useState(false);

  // Bio Generator State
  const [bioNiche, setBioNiche] = useState("");
  const [bioPlatform, setBioPlatform] = useState("instagram");
  const [bioTone, setBioTone] = useState("professional");
  const [generatedBio, setGeneratedBio] = useState(null);
  const [loadingBio, setLoadingBio] = useState(false);

  // Carousel Creator State
  const [carouselTopic, setCarouselTopic] = useState("");
  const [carouselSlides, setCarouselSlides] = useState(5);
  const [carouselStyle, setCarouselStyle] = useState("educational");
  const [generatedCarousel, setGeneratedCarousel] = useState(null);
  const [loadingCarousel, setLoadingCarousel] = useState(false);

  // Bio Generator Handler
  const handleGenerateBio = () => {
    if (!bioNiche.trim()) {
      toast.error("Please enter your niche/profession");
      return;
    }
    setLoadingBio(true);
    
    setTimeout(() => {
      const bios = {
        instagram: {
          professional: `${bioNiche} Expert | Helping you achieve more | DM for collabs | Link below`,
          casual: `Living the ${bioNiche} life | Tips & tricks daily | Let's connect!`,
          creative: `${bioNiche} enthusiast on a mission | Creating magic one post at a time`
        },
        twitter: {
          professional: `${bioNiche} professional | Sharing insights & industry news | Opinions are my own`,
          casual: `Passionate about ${bioNiche} | Tweet lover | Coffee addict`,
          creative: `Exploring the world of ${bioNiche} | Thoughts, ideas & chaos`
        },
        linkedin: {
          professional: `${bioNiche} Specialist | Helping businesses grow | Open to opportunities | Let's connect`,
          casual: `${bioNiche} enthusiast building meaningful connections | Always learning`,
          creative: `Transforming ideas into reality in ${bioNiche} | Innovation is key`
        }
      };
      
      setGeneratedBio({
        bio: bios[bioPlatform]?.[bioTone] || `${bioNiche} | Follow for more`,
        platform: bioPlatform,
        charCount: (bios[bioPlatform]?.[bioTone] || '').length,
        alternatives: Object.values(bios[bioPlatform] || {})
      });
      setLoadingBio(false);
      toast.success("Bio generated!");
    }, 500);
  };

  // Carousel Creator Handler
  const handleGenerateCarousel = () => {
    if (!carouselTopic.trim()) {
      toast.error("Please enter a topic");
      return;
    }
    setLoadingCarousel(true);
    
    setTimeout(() => {
      const slides = [];
      const templates = {
        educational: [
          { title: carouselTopic, subtitle: "A Complete Guide", type: "cover" },
          { title: "Why This Matters", content: "Understanding the importance of " + carouselTopic, type: "intro" },
          { title: "Key Point #1", content: "The first thing you need to know...", type: "content" },
          { title: "Key Point #2", content: "Here's another crucial aspect...", type: "content" },
          { title: "Key Point #3", content: "Don't forget about this...", type: "content" },
          { title: "Pro Tips", content: "Expert advice to help you succeed", type: "tips" },
          { title: "Quick Summary", content: "Remember these key takeaways", type: "summary" },
          { title: "Save & Share!", subtitle: "Follow for more content like this", type: "cta" }
        ],
        storytelling: [
          { title: "The " + carouselTopic + " Story", subtitle: "Swipe to discover", type: "cover" },
          { title: "It Started With...", content: "The beginning of the journey", type: "intro" },
          { title: "The Challenge", content: "What obstacles appeared", type: "content" },
          { title: "The Turning Point", content: "When everything changed", type: "content" },
          { title: "The Solution", content: "How it was overcome", type: "content" },
          { title: "The Result", content: "What was achieved", type: "result" },
          { title: "Your Turn", subtitle: "Start your journey today", type: "cta" }
        ]
      };
      
      const template = templates[carouselStyle] || templates.educational;
      for (let i = 0; i < Math.min(carouselSlides, template.length); i++) {
        slides.push({ ...template[i], slideNumber: i + 1 });
      }
      
      setGeneratedCarousel({
        topic: carouselTopic,
        style: carouselStyle,
        slides,
        totalSlides: slides.length
      });
      setLoadingCarousel(false);
      toast.success("Carousel created!");
    }, 600);
  };

  // Demo Analytics Data
  const instagramAnalytics = {
    followers: 12500,
    followersGrowth: 8.5,
    engagement: 4.2,
    posts: 156,
    recentPosts: [
      { id: 1, type: "image", likes: 856, comments: 45, reach: 4200, date: "2025-02-14" },
      { id: 2, type: "reel", likes: 2340, comments: 189, reach: 15600, date: "2025-02-12" },
      { id: 3, type: "carousel", likes: 1205, comments: 78, reach: 6800, date: "2025-02-10" }
    ]
  };

  const facebookAnalytics = {
    pageFollowers: 8900,
    pageReach: 45000,
    engagement: 3.8,
    posts: 89,
    recentPosts: [
      { id: 1, type: "link", reactions: 234, comments: 56, shares: 23, reach: 3400 },
      { id: 2, type: "video", reactions: 567, comments: 89, shares: 45, reach: 8900 },
      { id: 3, type: "photo", reactions: 345, comments: 34, shares: 12, reach: 2100 }
    ]
  };

  // Fetch scheduled posts and connected accounts on load
  useEffect(() => {
    if (isLoggedIn && activeTool === "scheduler") {
      fetchScheduledPosts();
    }
    if (isLoggedIn && activeTool === "connected-accounts") {
      fetchConnectedAccounts();
    }
  }, [isLoggedIn, activeTool]);

  const fetchScheduledPosts = async () => {
    setLoadingScheduled(true);
    try {
      const response = await axios.get(`${API}/social/posts`, { headers: { 'X-Skip-Auth-Error': 'true' } });
      setScheduledPosts(response.data.posts || []);
    } catch (error) {
      console.error("Failed to fetch posts");
    } finally {
      setLoadingScheduled(false);
    }
  };

  const fetchConnectedAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await axios.get(`${API}/social/accounts`, { headers: { 'X-Skip-Auth-Error': 'true' } });
      setConnectedAccounts(response.data.accounts || []);
    } catch (error) {
      console.error("Failed to fetch accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  const connectAccount = async (platform) => {
    setConnectingPlatform(platform);
    try {
      // Simulate OAuth - in real app, this would redirect to OAuth flow
      const response = await axios.post(`${API}/social/accounts/connect`, {
        platform,
        account_name: `My ${platform} Account`
      });
      toast.success(response.data.message);
      fetchConnectedAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect account");
    } finally {
      setConnectingPlatform(null);
    }
  };

  const disconnectAccount = async (platform) => {
    try {
      await axios.delete(`${API}/social/accounts/${platform}`);
      toast.success(`${platform} disconnected`);
      fetchConnectedAccounts();
    } catch (error) {
      toast.error("Failed to disconnect account");
    }
  };

  const socialTools = [
    {
      id: "connected-accounts",
      name: "Connected Accounts",
      icon: <Link2 className="w-5 h-5" />,
      description: "Manage your social media connections",
      badge: "NEW"
    },
    {
      id: "instagram-profile",
      name: "Instagram Profile Scraper",
      icon: <Instagram className="w-5 h-5" />,
      description: "Analyze any public Instagram profile",
      badge: "NEW"
    },
    {
      id: "hashtag-generator",
      name: "Hashtag Generator",
      icon: <Hash className="w-5 h-5" />,
      description: "Generate trending hashtags for your posts"
    },
    {
      id: "post-generator",
      name: "Social Post Generator",
      icon: <Share2 className="w-5 h-5" />,
      description: "Create engaging social media posts"
    },
    {
      id: "scheduler",
      name: "Post Scheduler",
      icon: <Calendar className="w-5 h-5" />,
      description: "Schedule posts for optimal times"
    },
    {
      id: "bio-generator",
      name: "Bio Generator",
      icon: <Edit className="w-5 h-5" />,
      description: "Create platform-specific bios",
      badge: "NEW"
    },
    {
      id: "carousel-creator",
      name: "Carousel Creator",
      icon: <Layers className="w-5 h-5" />,
      description: "Design Instagram carousels",
      badge: "NEW"
    },
    {
      id: "video-downloader",
      name: "Video Downloader",
      icon: <Download className="w-5 h-5" />,
      description: "Download Instagram & Facebook videos"
    },
    {
      id: "instagram-image-downloader",
      name: "Instagram Image Downloader",
      icon: <Image className="w-5 h-5" />,
      description: "Download images from Instagram posts",
      badge: "NEW"
    },
    {
      id: "instagram-analytics",
      name: "Instagram Analytics",
      icon: <Instagram className="w-5 h-5" />,
      description: "Track Instagram performance"
    },
    {
      id: "facebook-analytics",
      name: "Facebook Analytics",
      icon: <Facebook className="w-5 h-5" />,
      description: "Monitor Facebook engagement"
    },
    {
      id: "post-performance",
      name: "Post Performance",
      icon: <BarChart3 className="w-5 h-5" />,
      description: "Analyze post engagement metrics"
    }
  ];

  const handleGenerateHashtags = async () => {
    if (!hashtagTopic.trim()) {
      toast.error("Please enter a topic");
      return;
    }

    setLoadingHashtags(true);
    try {
      // Use dedicated hashtag API
      const response = await axios.post(`${API}/content/hashtags/generate`, {
        topic: hashtagTopic,
        platform: "instagram",
        count: 30
      }, { withCredentials: true });
      
      setGeneratedHashtags({
        topic: hashtagTopic,
        hashtags: response.data.hashtags || [],
        categories: response.data.categories,
        copyText: response.data.copy_text
      });
      
      // Save to search history
      try {
        await axios.post(`${BACKEND_URL}/api/user/search-history`, {
          tool: "Hashtag Generator",
          query: hashtagTopic,
          result_summary: `Generated ${response.data.total_count || 0} hashtags`
        }, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      } catch (e) {}
      
      toast.success("Hashtags generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate hashtags");
    } finally {
      setLoadingHashtags(false);
    }
  };

  // Bulk Hashtag Generation - process multiple topics at once
  const handleBulkHashtagGeneration = async () => {
    const topics = bulkHashtagTopics
      .split('\n')
      .map(t => t.trim())
      .filter(t => t.length > 0);

    if (topics.length === 0) {
      toast.error("Please enter at least one topic (one per line)");
      return;
    }

    if (topics.length > 10) {
      toast.error("Maximum 10 topics allowed at once");
      return;
    }

    setLoadingBulkHashtags(true);
    setBulkHashtagResults([]);
    
    const results = [];
    
    for (const topic of topics) {
      try {
        const response = await axios.post(`${API}/content/hashtags/generate`, {
          topic: topic,
          platform: "instagram",
          count: 20
        }, { withCredentials: true });
        
        results.push({
          topic: topic,
          success: true,
          hashtags: response.data.hashtags || [],
          copyText: response.data.copy_text,
          count: response.data.total_count || 0
        });
      } catch (error) {
        results.push({
          topic: topic,
          success: false,
          error: error.response?.data?.detail || "Failed to generate"
        });
      }
    }
    
    setBulkHashtagResults(results);
    const successCount = results.filter(r => r.success).length;
    toast.success(`Generated hashtags for ${successCount}/${topics.length} topics!`);
    setLoadingBulkHashtags(false);
  };

  const copyBulkHashtags = () => {
    const allHashtags = bulkHashtagResults
      .filter(r => r.success)
      .map(r => `### ${r.topic}\n${r.copyText || r.hashtags.join(' ')}`)
      .join('\n\n');
    navigator.clipboard.writeText(allHashtags);
    toast.success("All hashtags copied to clipboard!");
  };

  const handleGeneratePosts = async () => {
    if (!postTopic.trim()) {
      toast.error("Please enter a topic");
      return;
    }

    setLoadingPosts(true);
    try {
      const response = await axios.post(`${API}/content/generate`, {
        topic: postTopic,
        content_type: "social_post",
        tone: "casual",
        keywords: null,
        length: "medium"
      });
      
      setGeneratedPosts({
        topic: postTopic,
        content: response.data.content
      });
      toast.success("Social posts generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate posts");
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleVideoDownload = async () => {
    if (!videoUrl.trim()) {
      toast.error("Please enter a video URL");
      return;
    }
    setVideoLoading(true);
    setVideoResult(null);
    try {
      const response = await axios.post(`${API}/social/video-download`, {
        url: videoUrl.trim()
      });
      if (response.data?.success) {
        setVideoResult(response.data);
        toast.success("Download link generated");
      } else {
        toast.error("Unable to fetch video");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch video");
    } finally {
      setVideoLoading(false);
    }
  };

  // Instagram Image Download Handler
  const handleInstagramImageDownload = async () => {
    if (!igImageUrl.trim()) {
      toast.error("Please enter an Instagram post URL");
      return;
    }
    
    // Validate Instagram URL
    if (!igImageUrl.includes("instagram.com")) {
      toast.error("Please enter a valid Instagram URL");
      return;
    }
    
    setIgImageLoading(true);
    setIgImageResult(null);
    
    try {
      const response = await axios.post(`${API}/social/instagram/download-image`, {
        url: igImageUrl.trim()
      });
      
      if (response.data?.success) {
        setIgImageResult(response.data);
        toast.success("Images extracted successfully!");
      } else {
        toast.error("Unable to extract images from this post");
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || "Failed to extract images";
      toast.error(msg);
      setIgImageResult({
        success: false,
        post_url: igImageUrl,
        error: msg,
      });
    } finally {
      setIgImageLoading(false);
    }
  };

  // Post Scheduler Functions
  const addScheduledPost = async () => {
    if (!newPost.content.trim()) {
      toast.error("Please enter post content");
      return;
    }
    if (!newPost.scheduledDate || !newPost.scheduledTime) {
      toast.error("Please select date and time");
      return;
    }

    try {
      const response = await axios.post(`${API}/social/posts/schedule`, {
        content: newPost.content,
        platform: newPost.platform,
        scheduled_date: newPost.scheduledDate,
        scheduled_time: newPost.scheduledTime
      });
      
      setScheduledPosts([...scheduledPosts, response.data.post]);
      setNewPost({
        content: "",
        platform: "instagram",
        scheduledDate: "",
        scheduledTime: "",
        status: "scheduled"
      });
      toast.success("Post scheduled successfully!");
    } catch (error) {
      // Fallback to local state if not logged in
      const post = {
        id: Date.now(),
        ...newPost,
        createdAt: new Date().toISOString()
      };
      setScheduledPosts([...scheduledPosts, post]);
      setNewPost({
        content: "",
        platform: "instagram",
        scheduledDate: "",
        scheduledTime: "",
        status: "scheduled"
      });
      toast.success("Post scheduled locally (login to sync)");
    }
  };

  const deleteScheduledPost = async (id) => {
    try {
      await axios.delete(`${API}/social/posts/${id}`);
      setScheduledPosts(scheduledPosts.filter(post => post.id !== id));
      toast.success("Post deleted");
    } catch (error) {
      setScheduledPosts(scheduledPosts.filter(post => post.id !== id));
      toast.success("Post deleted");
    }
  };

  const getPlatformIcon = (platform) => {
    switch(platform) {
      case 'instagram': return <Instagram className="w-4 h-4 text-pink-500" />;
      case 'facebook': return <Facebook className="w-4 h-4 text-blue-600" />;
      case 'twitter': return <Twitter className="w-4 h-4 text-sky-500" />;
      case 'linkedin': return <Linkedin className="w-4 h-4 text-blue-700" />;
      default: return <Share2 className="w-4 h-4" />;
    }
  };

  const getPlatformColor = (platform) => {
    switch(platform) {
      case 'instagram': return 'bg-gradient-to-r from-purple-500 to-pink-500';
      case 'facebook': return 'bg-blue-600';
      case 'twitter': return 'bg-sky-500';
      case 'linkedin': return 'bg-blue-700';
      default: return 'bg-gray-500';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Instagram Profile Scraper Handler
  const handleScrapeIgProfile = async () => {
    if (!igUsername.trim()) {
      toast.error("Please enter an Instagram username");
      return;
    }

    setIgProfileLoading(true);
    try {
      const response = await axios.post(`${API}/social/instagram/profile`, {
        username: igUsername.trim().replace("@", "")
      }, { withCredentials: true });
      
      setIgProfileData(response.data);
      toast.success("Profile data fetched!");
      
      // Save to search history
      try {
        await axios.post(`${BACKEND_URL}/api/user/search-history`, {
          tool: "Instagram Profile Scraper",
          query: igUsername,
          result_summary: `${response.data.followers?.toLocaleString() || 0} followers`
        }, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      } catch (e) {}
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch profile. Make sure the account is public.");
    } finally {
      setIgProfileLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm mb-4" aria-label="Breadcrumb">
          <RouterLink to="/" className="flex items-center gap-1 text-gray-500 hover:text-pink-600 transition-colors">
            <Home className="w-4 h-4" />
            <span>Home</span>
          </RouterLink>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <RouterLink to="/tools" className="text-gray-500 hover:text-pink-600 transition-colors">
            Tools
          </RouterLink>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-900 font-medium">Social Media Tools</span>
        </nav>

        {/* Page Title — Premium */}
        <div className="mb-10">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#111111] rounded-2xl flex items-center justify-center">
              <Share2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-1">Social Suite</p>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#111111]" data-testid="page-title">Social Media Tools</h1>
              <p className="text-slate-600 mt-1.5">Boost your social media presence with powerful marketing tools</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Social Media Tools</h3>
              <div className="space-y-2">
                {socialTools.map((tool) => (
                  <button
                    key={tool.id}
                    onClick={() => !COMING_SOON_TABS.includes(tool.id) && setActiveTool(tool.id)}
                    data-testid={`tool-${tool.id}`}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                      COMING_SOON_TABS.includes(tool.id)
                        ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        : activeTool === tool.id
                          ? 'bg-pink-100 text-pink-700 font-medium'
                          : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {tool.icon}
                        <span className="text-sm">{tool.name}</span>
                      </div>
                      {COMING_SOON_TABS.includes(tool.id) && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Soon</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Hashtag Generator */}
            {activeTool === "hashtag-generator" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg flex items-center justify-center">
                    <Hash className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Hashtag Generator</h2>
                    <p className="text-gray-600">Generate trending hashtags for maximum reach</p>
                  </div>
                </div>

                <div className="flex gap-4 mb-6">
                  <Input
                    placeholder="Enter your topic or niche (e.g., fitness, travel, food)"
                    value={hashtagTopic}
                    onChange={(e) => setHashtagTopic(e.target.value)}
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && handleGenerateHashtags()}
                    data-testid="hashtag-topic-input"
                  />
                  <Button 
                    onClick={handleGenerateHashtags}
                    disabled={loadingHashtags}
                    className="bg-pink-600 hover:bg-pink-700"
                    data-testid="generate-hashtags-btn"
                  >
                    {loadingHashtags ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Hash className="w-4 h-4 mr-2" /> Generate</>
                    )}
                  </Button>
                </div>

                {generatedHashtags && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">
                        Generated {generatedHashtags.hashtags.length} Hashtags
                      </h3>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(generatedHashtags.hashtags.join(' '))}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy All
                      </Button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {generatedHashtags.hashtags.map((tag, idx) => (
                        <Badge 
                          key={idx} 
                          className="bg-pink-100 text-pink-800 hover:bg-pink-200 cursor-pointer"
                          onClick={() => copyToClipboard(tag)}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg mt-4">
                      <h4 className="font-medium mb-2">Tips for using hashtags:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Use 20-30 hashtags on Instagram for maximum reach</li>
                        <li>• Mix popular and niche-specific hashtags</li>
                        <li>• Rotate hashtag sets to avoid shadowbanning</li>
                        <li>• Use 3-5 hashtags on Twitter for best engagement</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* Bulk Hashtag Generation Section */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <Plus className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Bulk Hashtag Generation</h3>
                      <p className="text-sm text-gray-500">Generate hashtags for multiple topics at once (max 10)</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <Textarea
                      placeholder="Enter topics (one per line)&#10;Example:&#10;fitness motivation&#10;healthy recipes&#10;yoga meditation&#10;gym workout"
                      value={bulkHashtagTopics}
                      onChange={(e) => setBulkHashtagTopics(e.target.value)}
                      className="min-h-[120px]"
                      data-testid="bulk-hashtag-topics-input"
                    />
                    <div className="flex gap-3">
                      <Button 
                        onClick={handleBulkHashtagGeneration}
                        disabled={loadingBulkHashtags}
                        className="bg-violet-600 hover:bg-violet-700 flex-1"
                        data-testid="bulk-generate-hashtags-btn"
                      >
                        {loadingBulkHashtags ? (
                          <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing Topics...</>
                        ) : (
                          <><Hash className="w-4 h-4 mr-2" /> Generate Bulk Hashtags</>
                        )}
                      </Button>
                      {bulkHashtagResults.length > 0 && (
                        <Button 
                          variant="outline"
                          onClick={copyBulkHashtags}
                          data-testid="copy-bulk-hashtags-btn"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy All
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Bulk Results */}
                  {bulkHashtagResults.length > 0 && (
                    <div className="mt-6 space-y-4">
                      <h4 className="font-medium text-gray-800">
                        Results: {bulkHashtagResults.filter(r => r.success).length}/{bulkHashtagResults.length} successful
                      </h4>
                      {bulkHashtagResults.map((result, idx) => (
                        <div 
                          key={idx} 
                          className={`p-4 rounded-lg border ${result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-medium flex items-center gap-2">
                              {result.success ? (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              ) : (
                                <span className="w-4 h-4 text-red-600">✕</span>
                              )}
                              {result.topic}
                            </h5>
                            {result.success && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => copyToClipboard(result.copyText || result.hashtags.join(' '))}
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copy
                              </Button>
                            )}
                          </div>
                          {result.success ? (
                            <div className="flex flex-wrap gap-1">
                              {result.hashtags.slice(0, 15).map((tag, tidx) => (
                                <span key={tidx} className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded">
                                  {tag}
                                </span>
                              ))}
                              {result.hashtags.length > 15 && (
                                <span className="text-xs text-gray-500">+{result.hashtags.length - 15} more</span>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-red-600">{result.error}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Instagram Profile Scraper - NEW */}
            {activeTool === "instagram-profile" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Instagram className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Instagram Profile Scraper</h2>
                    <p className="text-gray-600">Analyze any public Instagram profile</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter Instagram username (e.g., virat.kohli)"
                      value={igUsername}
                      onChange={(e) => setIgUsername(e.target.value)}
                      className="flex-1"
                      data-testid="ig-username-input"
                    />
                    <Button 
                      onClick={handleScrapeIgProfile}
                      disabled={igProfileLoading}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                      data-testid="ig-scrape-btn"
                    >
                      {igProfileLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Instagram className="w-4 h-4 mr-2" /> Analyze Profile</>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">Note: Only public Instagram profiles can be analyzed</p>
                </div>

                {igProfileData && (
                  <div className="space-y-6">
                    {/* Profile Header */}
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-pink-200">
                      {igProfileData.profile_pic && (
                        <img 
                          src={igProfileData.profile_pic} 
                          alt={igProfileData.username}
                          className="w-20 h-20 rounded-full border-4 border-white shadow-lg"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-bold text-gray-900">@{igProfileData.username}</h3>
                          {igProfileData.is_verified && (
                            <CheckCircle2 className="w-5 h-5 text-blue-500" />
                          )}
                        </div>
                        <p className="text-gray-600">{igProfileData.full_name}</p>
                        {igProfileData.external_url && (
                          <a 
                            href={igProfileData.external_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-pink-600 hover:underline"
                          >
                            {igProfileData.external_url}
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-white border rounded-xl text-center">
                        <p className="text-3xl font-bold text-purple-600">{igProfileData.posts_count?.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">Posts</p>
                      </div>
                      <div className="p-4 bg-white border rounded-xl text-center">
                        <p className="text-3xl font-bold text-pink-600">{igProfileData.followers?.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">Followers</p>
                      </div>
                      <div className="p-4 bg-white border rounded-xl text-center">
                        <p className="text-3xl font-bold text-indigo-600">{igProfileData.following?.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">Following</p>
                      </div>
                    </div>

                    {/* Bio */}
                    {igProfileData.bio && (
                      <div className="p-4 bg-white border rounded-xl">
                        <h4 className="font-semibold text-gray-700 mb-2">Bio</h4>
                        <p className="text-gray-600 whitespace-pre-wrap">{igProfileData.bio}</p>
                      </div>
                    )}

                    {/* Engagement Estimate */}
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                      <h4 className="font-semibold text-green-700 mb-2">Engagement Insights</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Follower/Following Ratio</p>
                          <p className="text-xl font-bold text-green-600">
                            {igProfileData.following > 0 
                              ? (igProfileData.followers / igProfileData.following).toFixed(2) 
                              : igProfileData.followers}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Account Type</p>
                          <p className="text-xl font-bold text-green-600">
                            {igProfileData.is_verified ? "Verified" : 
                             igProfileData.followers > 100000 ? "Influencer" :
                             igProfileData.followers > 10000 ? "Creator" : "Regular"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Post Generator */}
            {activeTool === "post-generator" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <Share2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Social Post Generator</h2>
                    <p className="text-gray-600">Create engaging posts for all platforms</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <Input
                    placeholder="What do you want to post about?"
                    value={postTopic}
                    onChange={(e) => setPostTopic(e.target.value)}
                    data-testid="post-topic-input"
                  />
                  <Button 
                    onClick={handleGeneratePosts}
                    disabled={loadingPosts}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                    data-testid="generate-posts-btn"
                  >
                    {loadingPosts ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating Posts...</>
                    ) : (
                      <><Share2 className="w-4 h-4 mr-2" /> Generate Posts for All Platforms</>
                    )}
                  </Button>
                </div>

                {generatedPosts && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">Generated Posts</h3>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(generatedPosts.content)}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy All
                      </Button>
                    </div>

                    <div className="p-4 bg-white border rounded-lg whitespace-pre-wrap">
                      {generatedPosts.content}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Bio Generator */}
            {activeTool === "bio-generator" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Bio Generator</h2>
                    <p className="text-gray-600">Create platform-specific bios instantly</p>
                  </div>
                  <Badge className="ml-auto bg-gradient-to-r from-pink-500 to-rose-500 text-white">NEW</Badge>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Your Niche/Profession</label>
                    <Input placeholder="e.g., Digital Marketing" value={bioNiche} onChange={(e) => setBioNiche(e.target.value)} data-testid="bio-niche-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Platform</label>
                    <Select value={bioPlatform} onValueChange={setBioPlatform}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="twitter">Twitter/X</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Tone</label>
                    <Select value={bioTone} onValueChange={setBioTone}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="creative">Creative</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button onClick={handleGenerateBio} disabled={loadingBio} className="w-full bg-gradient-to-r from-pink-500 to-rose-600 mb-6" data-testid="generate-bio-btn">
                  {loadingBio ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Edit className="w-4 h-4 mr-2" /> Generate Bio</>}
                </Button>

                {generatedBio && (
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl">
                      <div className="flex justify-between items-start mb-2">
                        <Badge>{generatedBio.platform}</Badge>
                        <span className="text-xs text-gray-500">{generatedBio.charCount} chars</span>
                      </div>
                      <p className="text-lg font-medium">{generatedBio.bio}</p>
                      <Button variant="outline" size="sm" className="mt-3" onClick={() => copyToClipboard(generatedBio.bio)}>
                        <Copy className="w-4 h-4 mr-2" /> Copy
                      </Button>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium mb-2">Alternatives:</h4>
                      <div className="space-y-2">
                        {generatedBio.alternatives.filter(a => a !== generatedBio.bio).map((alt, i) => (
                          <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm flex justify-between items-center">
                            <span>{alt}</span>
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(alt)}><Copy className="w-3 h-3" /></Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Carousel Creator */}
            {activeTool === "carousel-creator" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Layers className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Carousel Creator</h2>
                    <p className="text-gray-600">Design Instagram carousels with AI</p>
                  </div>
                  <Badge className="ml-auto bg-gradient-to-r from-violet-500 to-purple-500 text-white">NEW</Badge>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium mb-2 block">Carousel Topic</label>
                    <Input placeholder="e.g., 5 Tips for Better Sleep" value={carouselTopic} onChange={(e) => setCarouselTopic(e.target.value)} data-testid="carousel-topic-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Style</label>
                    <Select value={carouselStyle} onValueChange={setCarouselStyle}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="educational">Educational</SelectItem>
                        <SelectItem value="storytelling">Storytelling</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button onClick={handleGenerateCarousel} disabled={loadingCarousel} className="w-full bg-gradient-to-r from-violet-500 to-purple-600 mb-6" data-testid="generate-carousel-btn">
                  {loadingCarousel ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Layers className="w-4 h-4 mr-2" /> Create Carousel</>}
                </Button>

                {generatedCarousel && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-violet-50 rounded-lg">
                      <span className="font-medium">{generatedCarousel.topic}</span>
                      <Badge>{generatedCarousel.totalSlides} slides</Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {generatedCarousel.slides.map((slide, i) => (
                        <div key={i} className="aspect-square p-3 bg-gradient-to-br from-violet-100 to-purple-100 rounded-xl flex flex-col justify-between text-center border-2 border-violet-200">
                          <div className="text-xs text-violet-600 font-medium">{slide.type.toUpperCase()}</div>
                          <div>
                            <p className="font-bold text-sm text-gray-900 line-clamp-2">{slide.title}</p>
                            {slide.subtitle && <p className="text-xs text-gray-500 mt-1">{slide.subtitle}</p>}
                          </div>
                          <div className="text-xs text-violet-500">Slide {slide.slideNumber}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Video Downloader */}
            {activeTool === "video-downloader" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Download className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Video Downloader</h2>
                    <p className="text-gray-600">Download public Instagram and Facebook videos</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <Input
                    placeholder="Paste Instagram/Facebook video URL"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    data-testid="video-download-url-input"
                  />
                  <Button
                    onClick={handleVideoDownload}
                    disabled={videoLoading}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    data-testid="video-download-btn"
                  >
                    {videoLoading ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Fetching Link...</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Generate Download Link</>
                    )}
                  </Button>
                </div>

                {videoResult && (
                  <div className="mt-6 space-y-4" data-testid="video-download-result">
                    <div className="flex items-start gap-4">
                      {videoResult.thumbnail && (
                        <img
                          src={videoResult.thumbnail}
                          alt={videoResult.title || "Video thumbnail"}
                          className="w-28 h-20 object-cover rounded-lg border"
                          data-testid="video-download-thumbnail"
                        />
                      )}
                      <div>
                        <p className="font-semibold text-gray-900" data-testid="video-download-title">
                          {videoResult.title || "Social Video"}
                        </p>
                        <p className="text-sm text-gray-500" data-testid="video-download-platform">
                          Platform: {videoResult.platform}
                        </p>
                        {videoResult.duration && (
                          <p className="text-sm text-gray-500">
                            Duration: {Math.round(videoResult.duration)}s
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <Button asChild className="bg-green-600 hover:bg-green-700">
                        <a
                          href={videoResult.download_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="video-download-link-btn"
                        >
                          <Download className="w-4 h-4 mr-2" /> Download Video
                        </a>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(videoResult.download_url)}
                        data-testid="video-download-copy-btn"
                      >
                        <Copy className="w-4 h-4 mr-2" /> Copy Link
                      </Button>
                    </div>

                    <p className="text-xs text-gray-500">
                      Works for public Instagram/Facebook posts. Private links may fail.
                    </p>
                  </div>
                )}
              </Card>
            )}

            {/* Instagram Image Downloader */}
            {activeTool === "instagram-image-downloader" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Image className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Instagram Image Downloader</h2>
                    <p className="text-gray-600">Download high-quality images from Instagram posts</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Instagram Post URL</label>
                    <Input
                      placeholder="https://www.instagram.com/p/ABC123..."
                      value={igImageUrl}
                      onChange={(e) => setIgImageUrl(e.target.value)}
                      data-testid="ig-image-url-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Paste the URL of any public Instagram post</p>
                  </div>
                  
                  <Button
                    onClick={handleInstagramImageDownload}
                    disabled={igImageLoading}
                    className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
                    data-testid="ig-image-download-btn"
                  >
                    {igImageLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting Images...</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Extract Images</>
                    )}
                  </Button>
                </div>

                {igImageResult && (
                  <div className="mt-6 space-y-4" data-testid="ig-image-result">
                    {/* Post Info */}
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                      <Instagram className="w-8 h-8 text-pink-500" />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">@{igImageResult.username}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> {igImageResult.likes?.toLocaleString()}</span>
                          <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> {igImageResult.comments?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Caption */}
                    {igImageResult.caption && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700 line-clamp-3">{igImageResult.caption}</p>
                      </div>
                    )}

                    {/* Images Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      {igImageResult.images?.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={img.url}
                            alt={`Instagram image ${idx + 1}`}
                            className="w-full h-48 object-cover rounded-lg border"
                            data-testid={`ig-image-${idx}`}
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              className="bg-white text-gray-900 hover:bg-gray-100"
                              asChild
                            >
                              <a href={img.url} target="_blank" rel="noreferrer" download>
                                <Download className="w-4 h-4 mr-1" /> Download
                              </a>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-white/20 border-white text-white hover:bg-white/30"
                              asChild
                            >
                              <a href={img.url} target="_blank" rel="noreferrer">
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </Button>
                          </div>
                          <Badge className="absolute top-2 right-2 bg-black/50">
                            {img.width}x{img.height}
                          </Badge>
                        </div>
                      ))}
                    </div>

                    {/* Download All */}
                    <div className="flex gap-3">
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          igImageResult.images?.forEach((img, idx) => {
                            const link = document.createElement('a');
                            link.href = img.url;
                            link.download = `instagram_image_${idx + 1}.jpg`;
                            link.target = '_blank';
                            link.click();
                          });
                          toast.success("Downloading all images...");
                        }}
                        data-testid="ig-download-all-btn"
                      >
                        <Download className="w-4 h-4 mr-2" /> Download All Images
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          copyToClipboard(igImageResult.images?.map(i => i.url).join('\n'));
                        }}
                      >
                        <Copy className="w-4 h-4 mr-2" /> Copy URLs
                      </Button>
                    </div>

                    <p className="text-xs text-gray-500 text-center">
                      Works for public Instagram posts only. Private accounts are not supported.
                    </p>
                  </div>
                )}

                {/* How it works */}
                <div className="mt-8 p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">How to Download Instagram Images</h3>
                  <ol className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 bg-pink-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                      Open Instagram and find the post you want to download
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 bg-pink-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                      Click the three dots (...) and select "Copy Link"
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 bg-pink-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                      Paste the URL above and click "Extract Images"
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 bg-pink-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">4</span>
                      Download individual images or all at once!
                    </li>
                  </ol>
                </div>
              </Card>
            )}

            {/* Connected Accounts */}
            {activeTool === "connected-accounts" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <Link2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Connected Accounts</h2>
                    <p className="text-gray-600">Manage your social media connections</p>
                  </div>
                </div>

                {!isLoggedIn ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Link2 className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600 mb-4">Login to connect your social accounts</p>
                    <Button onClick={() => navigate("/login")}>Login</Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Available Platforms */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { id: "instagram", name: "Instagram", icon: <Instagram className="w-6 h-6" />, color: "from-pink-500 to-purple-500" },
                        { id: "facebook", name: "Facebook", icon: <Facebook className="w-6 h-6" />, color: "from-blue-600 to-blue-700" },
                        { id: "twitter", name: "Twitter/X", icon: <Twitter className="w-6 h-6" />, color: "from-sky-400 to-sky-500" },
                        { id: "linkedin", name: "LinkedIn", icon: <Linkedin className="w-6 h-6" />, color: "from-blue-700 to-blue-800" }
                      ].map((platform) => {
                        const isConnected = connectedAccounts.some(a => a.platform === platform.id);
                        return (
                          <div key={platform.id} className={`p-4 rounded-lg border-2 ${isConnected ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-12 h-12 bg-gradient-to-br ${platform.color} rounded-lg flex items-center justify-center text-white`}>
                                  {platform.icon}
                                </div>
                                <div>
                                  <p className="font-semibold">{platform.name}</p>
                                  {isConnected ? (
                                    <p className="text-sm text-green-600 flex items-center gap-1">
                                      <CheckCircle2 className="w-3 h-3" /> Connected
                                    </p>
                                  ) : (
                                    <p className="text-sm text-gray-500">Not connected</p>
                                  )}
                                </div>
                              </div>
                              {isConnected ? (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => disconnectAccount(platform.id)}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  <Unlink className="w-4 h-4 mr-1" /> Disconnect
                                </Button>
                              ) : (
                                <Button 
                                  size="sm"
                                  onClick={() => connectAccount(platform.id)}
                                  disabled={connectingPlatform === platform.id}
                                  className={`bg-gradient-to-r ${platform.color} text-white`}
                                >
                                  {connectingPlatform === platform.id ? (
                                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <Link2 className="w-4 h-4 mr-1" />
                                  )}
                                  Connect
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Info Banner */}
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-medium text-blue-800 mb-2">How it works</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Connect your social accounts to schedule posts directly</li>
                        <li>• Posts will be published automatically at scheduled times</li>
                        <li>• View analytics and engagement across all platforms</li>
                      </ul>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Placeholder for other tools */}
            {!["hashtag-generator", "post-generator", "scheduler", "connected-accounts", "bio-generator", "carousel-creator", "video-downloader", "instagram-image-downloader", "instagram-analytics", "facebook-analytics", "post-performance", "instagram-profile"].includes(activeTool) && (
              <Card className="p-6 text-center">
                <div className="py-12">
                  <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {socialTools.find(t => t.id === activeTool)?.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {socialTools.find(t => t.id === activeTool)?.name}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {socialTools.find(t => t.id === activeTool)?.description}
                  </p>
                  <Badge>Coming Soon</Badge>
                </div>
              </Card>
            )}

            {/* Post Scheduler */}
            {activeTool === "scheduler" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Post Scheduler</h2>
                    <p className="text-gray-600">Schedule your social media posts</p>
                  </div>
                </div>

                <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="scheduler-coming-soon-banner">
                  <Badge className="bg-amber-500 text-white">Coming Soon</Badge>
                  <div>
                    <p className="text-sm font-medium text-amber-800">Social platform integrations are coming soon.</p>
                    <p className="text-xs text-amber-700">Abhi scheduled posts draft me save hote hain; auto-publish next update me enable hoga.</p>
                  </div>
                </div>

                {/* New Post Form */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="font-semibold mb-4">Schedule New Post</h3>
                  <div className="space-y-4">
                    <Textarea
                      placeholder="Write your post content here..."
                      value={newPost.content}
                      onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                      className="min-h-[100px]"
                      data-testid="post-content-input"
                    />
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Platform</label>
                        <Select 
                          value={newPost.platform} 
                          onValueChange={(value) => setNewPost({...newPost, platform: value})}
                        >
                          <SelectTrigger data-testid="platform-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="instagram">
                              <div className="flex items-center gap-2">
                                <Instagram className="w-4 h-4 text-pink-500" />
                                Instagram
                              </div>
                            </SelectItem>
                            <SelectItem value="facebook">
                              <div className="flex items-center gap-2">
                                <Facebook className="w-4 h-4 text-blue-600" />
                                Facebook
                              </div>
                            </SelectItem>
                            <SelectItem value="twitter">
                              <div className="flex items-center gap-2">
                                <Twitter className="w-4 h-4 text-sky-500" />
                                Twitter/X
                              </div>
                            </SelectItem>
                            <SelectItem value="linkedin">
                              <div className="flex items-center gap-2">
                                <Linkedin className="w-4 h-4 text-blue-700" />
                                LinkedIn
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Date</label>
                        <Input
                          type="date"
                          value={newPost.scheduledDate}
                          onChange={(e) => setNewPost({...newPost, scheduledDate: e.target.value})}
                          min={new Date().toISOString().split('T')[0]}
                          data-testid="schedule-date-input"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Time</label>
                        <Input
                          type="time"
                          value={newPost.scheduledTime}
                          onChange={(e) => setNewPost({...newPost, scheduledTime: e.target.value})}
                          data-testid="schedule-time-input"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => setShowPostPreview(!showPostPreview)}
                        className="flex-1"
                        disabled={!newPost.content.trim()}
                        data-testid="preview-post-btn"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {showPostPreview ? 'Hide Preview' : 'Preview Post'}
                      </Button>
                      <Button 
                        onClick={addScheduledPost}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        data-testid="schedule-post-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Schedule Post
                      </Button>
                    </div>

                    {/* Post Preview */}
                    {showPostPreview && newPost.content.trim() && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-xl border">
                        <h4 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          Post Preview - {newPost.platform.charAt(0).toUpperCase() + newPost.platform.slice(1)}
                        </h4>
                        
                        {/* Platform-specific preview */}
                        <div className={`bg-white rounded-lg border overflow-hidden ${
                          newPost.platform === 'twitter' ? 'max-w-md' : ''
                        }`}>
                          {/* Header */}
                          <div className="p-3 flex items-center gap-3 border-b">
                            <div className={`w-10 h-10 rounded-full ${getPlatformColor(newPost.platform)} flex items-center justify-center text-white`}>
                              {getPlatformIcon(newPost.platform)}
                            </div>
                            <div>
                              <p className="font-semibold text-sm">Your Brand</p>
                              <p className="text-xs text-gray-500">
                                {newPost.scheduledDate && newPost.scheduledTime 
                                  ? `Scheduled: ${new Date(newPost.scheduledDate + 'T' + newPost.scheduledTime).toLocaleString()}`
                                  : 'Just now'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Content */}
                          <div className="p-3">
                            <p className={`whitespace-pre-wrap text-gray-800 ${
                              newPost.platform === 'twitter' ? 'text-sm' : ''
                            }`}>
                              {newPost.content}
                            </p>
                          </div>
                          
                          {/* Footer based on platform */}
                          <div className="px-3 pb-3 flex items-center gap-4 text-gray-500 text-xs">
                            {newPost.platform === 'instagram' && (
                              <>
                                <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> Like</span>
                                <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> Comment</span>
                                <span className="flex items-center gap-1"><Send className="w-4 h-4" /> Share</span>
                              </>
                            )}
                            {newPost.platform === 'twitter' && (
                              <>
                                <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> Reply</span>
                                <span className="flex items-center gap-1"><RefreshCw className="w-4 h-4" /> Retweet</span>
                                <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> Like</span>
                              </>
                            )}
                            {newPost.platform === 'facebook' && (
                              <>
                                <span className="flex items-center gap-1"><ThumbsUp className="w-4 h-4" /> Like</span>
                                <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> Comment</span>
                                <span className="flex items-center gap-1"><Share2 className="w-4 h-4" /> Share</span>
                              </>
                            )}
                            {newPost.platform === 'linkedin' && (
                              <>
                                <span className="flex items-center gap-1"><ThumbsUp className="w-4 h-4" /> Like</span>
                                <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> Comment</span>
                                <span className="flex items-center gap-1"><Share2 className="w-4 h-4" /> Repost</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Character count */}
                        <div className="mt-2 text-xs text-gray-500 flex justify-between">
                          <span>Character count: {newPost.content.length}</span>
                          {newPost.platform === 'twitter' && newPost.content.length > 280 && (
                            <span className="text-red-500">Exceeds Twitter limit!</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Scheduled Posts List */}
                <div>
                  <h3 className="font-semibold mb-4">
                    Scheduled Posts ({scheduledPosts.length})
                  </h3>
                  
                  {scheduledPosts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No scheduled posts yet</p>
                      <p className="text-sm">Create your first scheduled post above</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {scheduledPosts.map((post) => (
                        <div 
                          key={post.id} 
                          className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`w-8 h-8 rounded-full ${getPlatformColor(post.platform)} flex items-center justify-center`}>
                                  {getPlatformIcon(post.platform)}
                                </div>
                                <span className="font-medium capitalize">{post.platform}</span>
                                <Badge variant="outline" className="ml-2">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {post.scheduledDate} at {post.scheduledTime}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-700 line-clamp-2">{post.content}</p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => deleteScheduledPost(post.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Best Times to Post */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    Best Times to Post
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="p-2 bg-white rounded text-center">
                      <Instagram className="w-5 h-5 mx-auto text-pink-500 mb-1" />
                      <p className="font-medium">Instagram</p>
                      <p className="text-gray-500">11am - 1pm</p>
                    </div>
                    <div className="p-2 bg-white rounded text-center">
                      <Facebook className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                      <p className="font-medium">Facebook</p>
                      <p className="text-gray-500">1pm - 4pm</p>
                    </div>
                    <div className="p-2 bg-white rounded text-center">
                      <Twitter className="w-5 h-5 mx-auto text-sky-500 mb-1" />
                      <p className="font-medium">Twitter</p>
                      <p className="text-gray-500">8am - 10am</p>
                    </div>
                    <div className="p-2 bg-white rounded text-center">
                      <Linkedin className="w-5 h-5 mx-auto text-blue-700 mb-1" />
                      <p className="font-medium">LinkedIn</p>
                      <p className="text-gray-500">7am - 8am</p>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Instagram Analytics */}
            {activeTool === "instagram-analytics" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <Instagram className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Instagram Analytics</h2>
                    <p className="text-gray-600">Track your Instagram performance</p>
                  </div>
                </div>

                {/* Overview Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Followers</p>
                    <p className="text-2xl font-bold text-pink-600">{instagramAnalytics.followers.toLocaleString()}</p>
                    <p className="text-xs text-green-600">+{instagramAnalytics.followersGrowth}% this month</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Engagement Rate</p>
                    <p className="text-2xl font-bold text-blue-600">{instagramAnalytics.engagement}%</p>
                    <p className="text-xs text-gray-500">Above average</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Total Posts</p>
                    <p className="text-2xl font-bold text-green-600">{instagramAnalytics.posts}</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Avg. Reach</p>
                    <p className="text-2xl font-bold text-orange-600">8.9K</p>
                  </div>
                </div>

                {/* Recent Posts */}
                <div>
                  <h4 className="font-semibold mb-3">Recent Post Performance</h4>
                  <div className="space-y-3">
                    {instagramAnalytics.recentPosts.map((post) => (
                      <div key={post.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <Badge className={
                            post.type === "reel" ? "bg-purple-100 text-purple-700" :
                            post.type === "carousel" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"
                          }>
                            {post.type}
                          </Badge>
                          <span className="text-sm text-gray-500">{post.date}</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-center">
                            <p className="font-semibold">{post.likes.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">Likes</p>
                          </div>
                          <div className="text-center">
                            <p className="font-semibold">{post.comments}</p>
                            <p className="text-xs text-gray-500">Comments</p>
                          </div>
                          <div className="text-center">
                            <p className="font-semibold">{post.reach.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">Reach</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  Note: Connect your Instagram Business account for real-time analytics data.
                </div>
              </Card>
            )}

            {/* Facebook Analytics */}
            {activeTool === "facebook-analytics" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                    <Facebook className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Facebook Analytics</h2>
                    <p className="text-gray-600">Monitor your Facebook page performance</p>
                  </div>
                </div>

                {/* Overview Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Page Followers</p>
                    <p className="text-2xl font-bold text-blue-600">{facebookAnalytics.pageFollowers.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Page Reach</p>
                    <p className="text-2xl font-bold text-green-600">{facebookAnalytics.pageReach.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Engagement</p>
                    <p className="text-2xl font-bold text-purple-600">{facebookAnalytics.engagement}%</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Total Posts</p>
                    <p className="text-2xl font-bold text-orange-600">{facebookAnalytics.posts}</p>
                  </div>
                </div>

                {/* Recent Posts */}
                <div>
                  <h4 className="font-semibold mb-3">Recent Post Performance</h4>
                  <div className="space-y-3">
                    {facebookAnalytics.recentPosts.map((post) => (
                      <div key={post.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <Badge className={
                          post.type === "video" ? "bg-red-100 text-red-700" :
                          post.type === "link" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                        }>
                          {post.type}
                        </Badge>
                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-center">
                            <p className="font-semibold">{post.reactions}</p>
                            <p className="text-xs text-gray-500">Reactions</p>
                          </div>
                          <div className="text-center">
                            <p className="font-semibold">{post.comments}</p>
                            <p className="text-xs text-gray-500">Comments</p>
                          </div>
                          <div className="text-center">
                            <p className="font-semibold">{post.shares}</p>
                            <p className="text-xs text-gray-500">Shares</p>
                          </div>
                          <div className="text-center">
                            <p className="font-semibold">{post.reach.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">Reach</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  Note: Connect your Facebook Page for real-time analytics data.
                </div>
              </Card>
            )}

            {/* Post Performance */}
            {activeTool === "post-performance" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Post Performance</h2>
                    <p className="text-gray-600">Analyze engagement across platforms</p>
                  </div>
                </div>

                {/* Performance Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Instagram className="w-5 h-5 text-pink-600" />
                      <span className="font-medium">Instagram</span>
                    </div>
                    <p className="text-2xl font-bold">4.2%</p>
                    <p className="text-sm text-gray-500">Avg. Engagement</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Facebook className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">Facebook</span>
                    </div>
                    <p className="text-2xl font-bold">3.8%</p>
                    <p className="text-sm text-gray-500">Avg. Engagement</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-sky-50 to-cyan-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Twitter className="w-5 h-5 text-sky-500" />
                      <span className="font-medium">Twitter</span>
                    </div>
                    <p className="text-2xl font-bold">2.9%</p>
                    <p className="text-sm text-gray-500">Avg. Engagement</p>
                  </div>
                </div>

                {/* Best Performing Content */}
                <div className="p-4 bg-green-50 rounded-lg mb-4">
                  <h4 className="font-semibold text-green-800 mb-2">Top Performing Content Types</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">1</p>
                      <p className="text-gray-600">Video/Reels</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">2</p>
                      <p className="text-gray-600">Carousels</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">3</p>
                      <p className="text-gray-600">Stories</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">Recommendations</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Post more video content - it's getting 3x more engagement</li>
                    <li>• Best posting time: 11am-1pm on weekdays</li>
                    <li>• Use 5-10 relevant hashtags for optimal reach</li>
                  </ul>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Related Tools Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <RelatedTools currentToolId={activeTool} category="social" />
        </div>
      </div>
      <ToolSEOContent toolId="social-media-tools" />
      <Footer />
      <WhatsAppChat />
    </div>
  );
}

export default SocialMediaToolsContent;
